import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Paper,
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
  Box,
  Alert,
  Grid,
  Card,
  CardContent,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SecurityIcon from "@mui/icons-material/Security";
import CircularLoading from "../components/CircularLoading";
import { getMenuItems } from "../config/menuConfig";
import { ref, get, set, getDatabase } from "firebase/database";

export default function RoleManagement() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [managerPermissions, setManagerPermissions] = useState({});
  const auth = useSelector((state) => state.auth);
  const database = getDatabase();

  // Get menu items dynamically from shared config (memoized to prevent infinite loops)
  const menuItems = React.useMemo(() => 
    getMenuItems(t).filter(item => 
      item.key !== 'logout' && 
      item.key !== 'profile'
    ), [t]
  );

  // Load manager permissions from database
  useEffect(() => {
    const loadPermissions = async () => {
      try {
        const managerRef = ref(database, "roles/manager/permissions");
        const snapshot = await get(managerRef);
        
        if (snapshot.exists()) {
          setManagerPermissions(snapshot.val());
        } else {
          // Initialize with all permissions disabled
          const initialPermissions = {};
          menuItems.forEach(item => {
            initialPermissions[item.key] = false;
            // Add sub-permissions
            if (item.sub_permissions) {
              Object.keys(item.sub_permissions).forEach(subKey => {
                initialPermissions[`${item.key}_${subKey}`] = false;
              });
            }
          });
          console.log("Initial permissions:", initialPermissions);
          setManagerPermissions(initialPermissions);
        }
        setLoading(false);
      } catch (error) {
        console.error("Detailed error loading permissions:", error);
        console.error("Error message:", error.message);
        console.error("Error code:", error.code);
        setMessage(`Error loading permissions: ${error.message}`);
        setLoading(false);
      }
    };

    loadPermissions();
  }, [database, menuItems]);

  // Handle permission toggle
  const handlePermissionToggle = (permissionKey) => {
    setManagerPermissions(prev => ({
      ...prev,
      [permissionKey]: !prev[permissionKey]
    }));
  };

  // Save permissions to database
  const handleSavePermissions = async () => {
    setSaving(true);
    setMessage("");

    try {
      const managerRef = ref(database, "roles/manager/permissions");
      await set(managerRef, managerPermissions);
      
      setMessage("Manager permissions updated successfully!");
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Error saving permissions:", error);
      setMessage("Error saving permissions!");
    }
    
    setSaving(false);
  };

  // Enable all permissions
  const handleEnableAll = () => {
    const allEnabled = {};
    menuItems.forEach(item => {
      allEnabled[item.key] = true;
      if (item.sub_permissions) {
        Object.keys(item.sub_permissions).forEach(subKey => {
          allEnabled[`${item.key}_${subKey}`] = true;
        });
      }
    });
    setManagerPermissions(allEnabled);
  };

  // Disable all permissions
  const handleDisableAll = () => {
    const allDisabled = {};
    menuItems.forEach(item => {
      allDisabled[item.key] = false;
      if (item.sub_permissions) {
        Object.keys(item.sub_permissions).forEach(subKey => {
          allDisabled[`${item.key}_${subKey}`] = false;
        });
      }
    });
    setManagerPermissions(allDisabled);
  };

  // Check if user is admin
  if (!auth.profile || auth.profile.usertype !== "admin") {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Access Denied: Only Admin can manage roles.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return <CircularLoading />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <SecurityIcon sx={{ mr: 2, color: 'primary.main', fontSize: 32 }} />
          <Box>
            <Typography variant="h4" gutterBottom color="primary">
              Role Management
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
              Configure which menu items Manager role can access
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Message */}
        {message && (
          <Alert
            severity={message.includes("Error") ? "error" : "success"}
            sx={{ mb: 2 }}
          >
            {message}
          </Alert>
        )}

        {/* Menu Items List */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Available Menu Items
            </Typography>

            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              These are all the menu items available in the sidebar:
            </Typography>

            <Grid container spacing={2}>
              {menuItems.map((item, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  {/* Check if this permission has sub-permissions */}
                  {item.sub_permissions ? (
                    <Accordion sx={{ boxShadow: 1, mb: 1 }}>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{ 
                          minHeight: 48,
                          '&.Mui-expanded': { minHeight: 48 }
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={managerPermissions[item.key] || false}
                              onChange={() => handlePermissionToggle(item.key)}
                              color="primary"
                              onClick={(e) => e.stopPropagation()}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                {item.name}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                Key: {item.key}
                              </Typography>
                            </Box>
                          }
                          onClick={(e) => e.stopPropagation()}
                          sx={{ mr: 1 }}
                        />
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        <Box sx={{ pl: 2 }}>
                          {Object.entries(item.sub_permissions).map(([subKey, subPermission]) => (
                            <FormControlLabel
                              key={subKey}
                              control={
                                <Checkbox
                                  checked={managerPermissions[`${item.key}_${subKey}`] || false}
                                  onChange={() => handlePermissionToggle(`${item.key}_${subKey}`)}
                                  color="secondary"
                                  size="small"
                                />
                              }
                              label={
                                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                  {subPermission.name}
                                </Typography>
                              }
                              sx={{ display: 'block', mb: 0.5 }}
                            />
                          ))}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  ) : (
                    /* Regular permission without sub-permissions */
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={managerPermissions[item.key] || false}
                          onChange={() => handlePermissionToggle(item.key)}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {item.name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Key: {item.key} | Access: {item.access.join(', ')}
                          </Typography>
                        </Box>
                      }
                    />
                  )}
                </Grid>
              ))}
            </Grid>

            <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSavePermissions}
                disabled={saving}
                size="large"
              >
                {saving ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Saving...
                  </>
                ) : (
                  "Save Permissions"
                )}
              </Button>

              <Button
                variant="outlined"
                onClick={handleEnableAll}
                disabled={saving}
              >
                Enable All
              </Button>

              <Button
                variant="outlined"
                onClick={handleDisableAll}
                disabled={saving}
              >
                Disable All
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Paper>
    </Box>
  );
}